/**
 * Algolia adapter for React InstantSearch
 *
 * Re-exports the app's single shared lite client (from
 * `@/lib/search/algolia/client`) so InstantSearch, the search services, and
 * autocomplete all share one client and one persistent response cache instead
 * of maintaining separate instances.
 */

import { algoliaSearchClient } from '@/lib/search/algolia/client';

export const searchClient = algoliaSearchClient;

/**
 * Algolia index names
 */
export const ALGOLIA_INDICES = {
  PRIMARY: 'sequences',
  CREATED_AT_DESC: 'sequences_created_at_desc',
  PRICE_ASC: 'sequences_price_asc',
  PRICE_DESC: 'sequences_price_desc',
  FREE_UPVOTES: 'sequences_free_upvotes',
} as const;

export type AlgoliaIndexName = typeof ALGOLIA_INDICES[keyof typeof ALGOLIA_INDICES];

/**
 * Get the appropriate index name based on sort option
 */
export function getAlgoliaIndexName(sortBy?: string): AlgoliaIndexName {
  switch (sortBy) {
    case 'created_at:desc':
    case 'sequences_v2/sort/created_at:desc':
      return ALGOLIA_INDICES.CREATED_AT_DESC;

    case 'price:asc':
    case 'sequences_v2/sort/price:asc':
      return ALGOLIA_INDICES.PRICE_ASC;

    case 'price:desc':
    case 'sequences_v2/sort/price:desc':
      return ALGOLIA_INDICES.PRICE_DESC;

    case 'upvote_count:desc':
    case 'upvote_count:desc,_text_match:desc':
    case 'sequences_v2/sort/upvote_count:desc':
      return ALGOLIA_INDICES.FREE_UPVOTES;

    default:
      return ALGOLIA_INDICES.PRIMARY;
  }
}

/**
 * Transform legacy facet names to Algolia attribute names
 * For compatibility with existing UI code
 */
export function transformFacetName(legacyFacet: string): string {
  // Algolia uses the same attribute names, so no transformation needed
  return legacyFacet;
}

/**
 * Transform legacy sort option to Algolia index name
 */
export function transformSortOption(legacySort: string): string {
  // Map legacy sort options to Algolia replica indices
  const mapping: Record<string, string> = {
    'sequences_v2': ALGOLIA_INDICES.PRIMARY,
    'sequences_v2/sort/download_count:desc': ALGOLIA_INDICES.PRIMARY, // Use primary with hot_score
    'sequences_v2/sort/created_at:desc': ALGOLIA_INDICES.CREATED_AT_DESC,
    'sequences_v2/sort/price:asc': ALGOLIA_INDICES.PRICE_ASC,
    'sequences_v2/sort/price:desc': ALGOLIA_INDICES.PRICE_DESC,
    'sequences_v2/sort/upvote_count:desc': ALGOLIA_INDICES.FREE_UPVOTES,
  };

  return mapping[legacySort] || ALGOLIA_INDICES.PRIMARY;
}
